import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent, KeyboardEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useDataSync } from '../context/DataSyncContext';
import type { Flow, FlowInput, TagSummary } from '../types';
import {
  analyzeFlowStructure,
  extractFlowSubset,
  selectNodesSegment,
  type FlowStructureAnalysis
} from '../utils/flowAnalysis';
import '../styles/flow.css';

type Mode = 'create' | 'edit';

const defaultForm: FlowInput = {
  title: '',
  description: '',
  prerequisites: '',
  kind: 'flow',
  tags: [],
  flowType: null,
  flowTypeDetail: null,
  jsonText: '',
  notes: '',
  visibility: 'public',
  visibleTeamIds: []
};

export function FlowFormPage({ mode = 'create' }: { mode?: Mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshAllBadges } = useDataSync();
  const [form, setForm] = useState<FlowInput>(defaultForm);
  const [entry, setEntry] = useState<Flow | null>(null);
  const [availableTags, setAvailableTags] = useState<TagSummary[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [moderationNotes, setModerationNotes] = useState('');
  const [restoreSnapshot, setRestoreSnapshot] = useState(true);
  const [jsonImportError, setJsonImportError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedFlow, setParsedFlow] = useState<unknown>(null);
  const [analysis, setAnalysis] = useState<FlowStructureAnalysis | null>(null);
  const [extractionMode, setExtractionMode] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [rangeStartId, setRangeStartId] = useState('');
  const [rangeEndId, setRangeEndId] = useState('');
  const [isListFormat, setIsListFormat] = useState(false);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [teamError, setTeamError] = useState<string | null>(null);
  const tagInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const fetchedTags = await api.tags();
        if (isActive) {
          setAvailableTags(fetchedTags);
        }
        const myTeams = await api.myTeams();
        if (isActive) {
          setTeams(myTeams.teams || []);
        }
      } catch (err) {
        console.error('Failed to load tags', err);
        setTeamError((err as Error).message);
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !id) {
      return;
    }

    let isActive = true;
    setLoading(true);

    (async () => {
      try {
        const existing = await api.flow(id);
        if (!isActive) {
          return;
        }
        hydrateForm(existing);
      } catch (err) {
        if (isActive) {
          setError((err as Error).message);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [id, mode]);

  // Pre-populate form when navigating from "Submit to Marketplace" (create mode with prefill state)
  useEffect(() => {
    if (mode !== 'create') return;
    const state = location.state as { prefill?: { title?: string; description?: string; prerequisites?: string; kind?: string; tags?: string[]; json?: unknown } } | null;
    const prefill = state?.prefill;
    if (!prefill) return;

    const normalizedKind = prefill.kind === 'node' ? 'node' : 'flow';
    setForm({
      title: prefill.title || '',
      description: prefill.description || '',
      prerequisites: prefill.prerequisites || '',
      kind: normalizedKind,
      tags: prefill.tags || [],
      flowType: null,
      flowTypeDetail: null,
      jsonText: prefill.json ? JSON.stringify(prefill.json, null, 2) : '',
      notes: '',
      visibility: 'public',
      visibleTeamIds: []
    });
    setTagInput('');
  }, [mode, location.state]);

  const hydrateForm = (flow: Flow) => {
    const normalizedKind = flow.kind === 'node' ? 'node' : 'flow';

    const nextForm: FlowInput = {
      title: flow.title,
      description: flow.description,
      prerequisites: flow.prerequisites ?? '',
      kind: normalizedKind,
      tags: flow.tags || [],
      flowType: null,
      flowTypeDetail: null,
      jsonText: JSON.stringify(flow.json ?? {}, null, 2),
      notes: '',
      visibility: flow.visibility || 'public',
      visibleTeamIds: flow.visibleTeamIds || []
    };
    setForm(nextForm);
    setTagInput('');
    setEntry(flow);
  };

  const updateField = <Key extends keyof FlowInput>(field: Key, value: FlowInput[Key]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const availableTagLookup = useMemo<Map<string, string>>(() => {
    const lookup = new Map<string, string>();
    availableTags.forEach((entry) => {
      lookup.set(entry.name.toLowerCase(), entry.name);
    });
    form.tags.forEach((tag) => {
      lookup.set(tag.toLowerCase(), tag);
    });
    return lookup;
  }, [availableTags, form.tags]);

  const mergeTag = (currentTags: string[], rawTag: string) => {
    if (!rawTag) {
      return currentTags;
    }

    const cleaned = rawTag.trim().replace(/\s+/g, ' ');
    if (!cleaned) {
      return currentTags;
    }

    const key = cleaned.toLowerCase();
    if (currentTags.some((tag) => tag.toLowerCase() === key)) {
      return currentTags;
    }

    const canonical = availableTagLookup.get(key) || cleaned;

    if (!availableTagLookup.has(key)) {
      setAvailableTags((existing) => {
        if (existing.some((entry) => entry.name.toLowerCase() === canonical.toLowerCase())) {
          return existing;
        }
        return [...existing, { name: canonical, count: 0 }];
      });
    }

    return [...currentTags, canonical];
  };

  const addTag = (rawTag: string) => {
    const nextTags = mergeTag(form.tags, rawTag);
    if (nextTags !== form.tags) {
      updateField('tags', nextTags);
    }
    setTagInput('');
    tagInputRef.current?.focus();
  };

  const removeTag = (tagToRemove: string) => {
    const nextTags = form.tags.filter(
      (tag) => tag.toLowerCase() !== tagToRemove.toLowerCase()
    );
    if (nextTags.length !== form.tags.length) {
      updateField('tags', nextTags);
    }
    tagInputRef.current?.focus();
  };

  const handleTagInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTagInput(event.target.value);
  };

  const filteredSuggestions = useMemo(() => {
    const exclude = new Set(form.tags.map((tag) => tag.toLowerCase()));
    const query = tagInput.trim().toLowerCase();

    return availableTags
      .map((entry) => entry.name)
      .filter((name) => !exclude.has(name.toLowerCase()))
      .filter((name) => (query ? name.toLowerCase().includes(query) : true))
      .slice(0, 8);
  }, [availableTags, form.tags, tagInput]);

  const handleTagInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',') {
      const candidate = tagInput;
      if (candidate.trim()) {
        event.preventDefault();
        addTag(candidate);
      }
      return;
    }

    if (event.key === 'Backspace' && tagInput === '') {
      const lastTag = form.tags[form.tags.length - 1];
      if (lastTag) {
        event.preventDefault();
        removeTag(lastTag);
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
  };

  const handleKindChange = (value: 'flow' | 'node') => {
    setForm((current) =>
      value === 'node'
        ? { ...current, kind: 'node', flowType: null, flowTypeDetail: null }
        : { ...current, kind: 'flow', flowType: null, flowTypeDetail: null }
    );
  };


  const importJsonFile = useCallback(
    (file: File) => {
      if (!file) {
        return;
      }
      const name = file.name.toLowerCase();
      if (
        file.type &&
        file.type !== 'application/json' &&
        !name.endsWith('.json')
      ) {
        setJsonImportError('Only JSON files are supported.');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        if (!result.trim()) {
          setJsonImportError('The selected file is empty.');
          return;
        }
        setJsonImportError(null);
        setSelectionError(null);
        setExtractionMode(false);
        updateField('jsonText', result);
      };
      reader.onerror = () => {
        setJsonImportError('Failed to read the selected file.');
      };
      reader.readAsText(file);
    },
    [updateField]
  );

  const handleJsonFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importJsonFile(file);
    }
    event.target.value = '';
  };

  useEffect(() => {
    if (!form.jsonText || !form.jsonText.trim()) {
      setParseError(null);
      setParsedFlow(null);
      setAnalysis(null);
      setSelectedNodeIds(new Set());
      setIsListFormat(false);
      return;
    }

    try {
      const parsed = JSON.parse(form.jsonText);
      setParsedFlow(parsed);
      setParseError(null);
      const structure = analyzeFlowStructure(parsed);
      setAnalysis(structure);
      const listFormatted =
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        Array.isArray((parsed as { list?: unknown[] }).list);
      setIsListFormat(Boolean(listFormatted));
      if (listFormatted && form.kind !== 'node') {
        setForm((current) => ({
          ...current,
          kind: 'node',
          flowType: null,
          flowTypeDetail: null
        }));
      }
    } catch (err) {
      console.error('Flow JSON parse error', err);
      setParsedFlow(null);
      setAnalysis(null);
      setParseError('Unable to parse JSON. Fix syntax to analyze the flow.');
      setIsListFormat(false);
    }
  }, [form.jsonText, form.kind]);

  useEffect(() => {
    if (!analysis) {
      setSelectedNodeIds(new Set());
      return;
    }

    setSelectedNodeIds((current) => {
      if (current.size === 0) {
        return new Set(analysis.nodes.map((node) => node.id));
      }

      const next = new Set<string>();
      analysis.nodes.forEach((node) => {
        if (current.has(node.id)) {
          next.add(node.id);
        }
      });

      if (next.size === 0) {
        return new Set(analysis.nodes.map((node) => node.id));
      }

      if (
        next.size === current.size &&
        next.size === analysis.nodes.length
      ) {
        return current;
      }

      return next;
    });
  }, [analysis]);

  useEffect(() => {
    const hasFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const handleDragEnter = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      dragCounterRef.current += 1;
      setIsDraggingFile(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDragLeave = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDraggingFile(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingFile(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        importJsonFile(file);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [importJsonFile]);

const toggleNodeSelection = (nodeId: string) => {
  setSelectedNodeIds((current) => {
    const next = new Set(current);
    if (next.has(nodeId)) {
      next.delete(nodeId);
    } else {
      next.add(nodeId);
    }
    return next;
  });
  setSelectionError(null);
};

const selectAllNodes = () => {
  if (!analysis) {
    return;
  }
  setSelectionError(null);
  setSelectedNodeIds(new Set(analysis.nodes.map((node) => node.id)));
};

const clearNodeSelection = () => {
  setSelectionError(null);
  setSelectedNodeIds(new Set());
};

const expandSelection = () => {
  if (!analysis || selectedNodeIds.size === 0) {
    return;
  }
  setSelectionError(null);
  const adjacency = new Map<string, Set<string>>();
  analysis.edges.forEach((edge) => {
    if (!adjacency.has(edge.sourceId)) {
      adjacency.set(edge.sourceId, new Set());
    }
    adjacency.get(edge.sourceId)?.add(edge.targetId);
    if (!adjacency.has(edge.targetId)) {
      adjacency.set(edge.targetId, new Set());
    }
    adjacency.get(edge.targetId)?.add(edge.sourceId);
  });
  const visited = new Set<string>(selectedNodeIds);
  const queue = Array.from(selectedNodeIds);
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }
    adjacency.get(current)?.forEach((neighbor) => {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    });
  }
  setSelectedNodeIds(new Set(visited));
};

const selectedNodeCount = selectedNodeIds.size;

const selectedEdgeCount = useMemo(() => {
  if (!analysis || selectedNodeIds.size === 0) {
    return 0;
  }
  const selection = new Set(Array.from(selectedNodeIds));
  return analysis.edges.filter(
    (edge) =>
      selection.has(edge.sourceId) && selection.has(edge.targetId)
  ).length;
}, [analysis, selectedNodeIds]);

const structureHint = useMemo(() => {
  if (!analysis || analysis.nodeCount === 0) {
    return null;
  }
  if (analysis.nodeCount === 1 && form.kind === 'flow') {
    return 'Detected a single node in the JSON. Switch the content type to Node to publish it individually.';
  }
  if (analysis.nodeCount > 1 && form.kind === 'node') {
    return `Detected ${analysis.nodeCount} interconnected nodes. This node entry will be treated as a series of nodes.`;
  }
  if (analysis.nodeCount > 1) {
    return `Detected ${analysis.nodeCount} nodes linked by ${analysis.edges.length} connections.`;
  }
  return null;
}, [analysis, form.kind]);

  const handleApplyExtraction = () => {
    if (!analysis || !parsedFlow) {
      return;
    }
    if (selectedNodeIds.size === 0) {
    setSelectionError('Select at least one node to build a subset.');
    return;
  }
  const result = extractFlowSubset(parsedFlow, selectedNodeIds);
    if (!result) {
      setSelectionError('Unable to extract the selected nodes.');
      return;
    }

    let outputJson = result.json;
    const listifiedJson = (() => {
      if (result.json && typeof result.json === 'object') {
        const content = (result.json as Record<string, unknown>).content;
        const nodesContainer =
          content && typeof content === 'object' && 'nodes' in content
            ? (content as Record<string, Record<string, unknown>>).nodes
            : (result.json as Record<string, Record<string, unknown>>).nodes;

        if (nodesContainer && typeof nodesContainer === 'object') {
          const entries = result.summary.keptNodeIds
            .map((id) => nodesContainer[id])
            .filter((entry): entry is unknown => typeof entry !== 'undefined');

          if (entries.length > 0) {
            return { list: entries };
          }
        }
      }
      return outputJson;
    })();

    const prettyJson = JSON.stringify(listifiedJson, null, 2);
    updateField('jsonText', prettyJson);
  setSelectionError(null);
  setExtractionMode(false);
  if (result.summary.nodeCount === 1) {
    handleKindChange('node');
  } else if (form.kind === 'node' && result.summary.nodeCount > 1) {
    handleKindChange('flow');
  }
};

const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const pendingTag = tagInput.trim();
    let tagsForSubmit = form.tags;
    if (pendingTag) {
      const merged = mergeTag(form.tags, pendingTag);
      if (merged !== form.tags) {
        updateField('tags', merged);
      }
      tagsForSubmit = merged;
      setTagInput('');
    }

    const normalizedPrerequisites = form.prerequisites.trim();

    const payload: FlowInput = {
      title: form.title,
      description: form.description,
      prerequisites: normalizedPrerequisites,
      kind: form.kind,
      tags: tagsForSubmit,
      flowType: null,
      flowTypeDetail: null,
      jsonText: form.jsonText,
      notes: form.notes,
      visibility: form.visibility,
      visibleTeamIds: form.visibility === 'team_restricted' ? form.visibleTeamIds : []
    };

    try {
      if (mode === 'edit' && id) {
        const updated = await api.updateFlow(id, payload);
        void refreshAllBadges();
        navigate(`/flows/${updated.id}`);
      } else {
        const created = await api.createFlow(payload);
        void refreshAllBadges();
        navigate(`/flows/${created.id}`);
      }
    } catch (submitError) {
      setError((submitError as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectRange = () => {
    if (!analysis) {
      return;
    }
    const startValue = rangeStartId.trim();
    const endValue = rangeEndId.trim();
    if (!startValue || !endValue) {
      setSelectionError('Enter both start and end node IDs to select a segment.');
      return;
    }

    const result = selectNodesSegment(analysis, startValue, endValue);
    if (!result.success) {
      setSelectionError(result.error);
      return;
    }

    if (result.nodeIds.length === 0) {
      setSelectionError('No nodes found between the specified IDs.');
      return;
    }

    setSelectedNodeIds(new Set(result.nodeIds));
    setSelectionError(null);
  };

  const isNode = form.kind === 'node';
  const jsonHelperMessage = useMemo(() => {
    const trimmed = form.jsonText.trim();
    if (!trimmed) {
      return null;
    }
    if (isNode) {
      return 'This node is copy/paste ready for import.';
    }
    if (isListFormat) {
      return 'This flow subset is copy/paste ready for import.';
    }
    return 'Download this flow as a JSON file before importing it elsewhere.';
  }, [form.jsonText, isNode, isListFormat]);

  const pageTitle = mode === 'edit' ? `Update ${isNode ? 'node' : 'flow'}` : `Create a new ${isNode ? 'node' : 'flow'}`;
  const submitLabel = mode === 'edit'
    ? 'Save changes'
    : form.visibility === 'team_restricted'
      ? 'Share with teams'
      : 'Submit for review';
  const canUnapprove = mode === 'edit' && user?.role === 'admin' && entry?.status === 'approved';
  const canRestoreSnapshot = Boolean(entry?.publishedVersion !== null);

  useEffect(() => {
    if (entry?.status === 'approved' && canRestoreSnapshot) {
      setRestoreSnapshot(true);
    } else if (!canRestoreSnapshot) {
      setRestoreSnapshot(false);
    }
  }, [entry?.status, canRestoreSnapshot]);

  const handleUnapprove = async () => {
    if (!id || !entry) {
      return;
    }

    setUnapproving(true);
    setError(null);

    try {
      const result = await api.unapproveFlow(id, {
        notes: moderationNotes || undefined,
        restoreApprovedSnapshot: restoreSnapshot && canRestoreSnapshot ? true : undefined
      });
      setEntry(result);
      hydrateForm(result);
      setModerationNotes('');
      if (result.status !== 'approved') {
        setRestoreSnapshot(false);
      }
      void refreshAllBadges();
    } catch (unapproveError) {
      setError((unapproveError as Error).message);
    } finally {
      setUnapproving(false);
    }
  };

  if (loading) {
    return <div className="loading">Preparing flow editor...</div>;
  }

  if (user?.role !== 'admin' && user?.role !== 'user') {
    return <div className="alert alert-error">You do not have access to this area.</div>;
  }

  return (
    <div className="flow-form">
      {isDraggingFile ? (
        <div className="flow-form__drop-overlay">
          <div className="flow-form__drop-message">
            Drop a JSON file to populate the flow definition
          </div>
        </div>
      ) : null}
      <h1>{pageTitle}</h1>
      <p className="flow-form__description">
        Provide a concise title, include tags for discovery, and paste the JSON representing the
        {isNode ? ' node' : ' flow'}. Public submissions require admin approval. Team-restricted
        entries are shared immediately.
      </p>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {mode === 'edit' && user?.role === 'admin' ? (
        <div className="flow-detail__moderation flow-form__admin-panel">
          <h3>Admin actions</h3>
          <p className="flow-detail__moderation-help">
            Current status:{' '}
            <span className={`status status-${entry?.status ?? 'pending'}`}>
              {entry?.status ?? 'pending'}
            </span>
          </p>
          {canUnapprove ? (
            <>
              <textarea
                placeholder="Add context for the author (optional)"
                value={moderationNotes}
                onChange={(event) => setModerationNotes(event.target.value)}
              />
              {canRestoreSnapshot ? (
                <label className="flow-detail__moderation-option">
                  <input
                    type="checkbox"
                    checked={restoreSnapshot}
                    onChange={(event) => setRestoreSnapshot(event.target.checked)}
                    disabled={unapproving}
                  />
                  Restore last approved snapshot
                </label>
              ) : null}
              <div className="flow-detail__moderation-actions">
                <button
                  className="button button-tertiary"
                  type="button"
                  onClick={handleUnapprove}
                  disabled={unapproving}
                >
                  {unapproving ? 'Marking as pending…' : 'Mark as pending'}
                </button>
              </div>
            </>
          ) : (
            <p className="flow-detail__moderation-help">
              Once this entry is approved you can mark it pending again from here if an issue is found.
            </p>
          )}
        </div>
      ) : null}

      <form className="form" onSubmit={handleSubmit}>
        <div className="form-row">
          <label>Content type</label>
          <p className="flow-form__structure-hint">
            {structureHint ?? 'We will detect whether this is a flow or node from the JSON definition.'}
          </p>
        </div>

        <div className="form-row">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            value={form.title}
            onChange={(event) => updateField('title', event.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <label htmlFor="description">Description</label>
          <input
            id="description"
            value={form.description}
            onChange={(event) => updateField('description', event.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <label htmlFor="prerequisites">
            Prerequisites <span className="label-optional">(optional)</span>
          </label>
          <textarea
            id="prerequisites"
            rows={3}
            value={form.prerequisites}
            onChange={(event) => updateField('prerequisites', event.target.value)}
            placeholder="List any dependencies, required configurations, or before-you-start steps"
          />
        </div>

        <div className="form-row">
          <label htmlFor="tags-input">Tags</label>
          <div
            className="tag-input"
            onClick={() => tagInputRef.current?.focus()}
            role="presentation"
          >
            {form.tags.map((tag) => (
              <button
                type="button"
                key={tag}
                className="chip chip-selected tag-input__chip"
                onClick={() => removeTag(tag)}
                aria-label={`Remove tag ${tag}`}
              >
                <span>{tag}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
            <input
              id="tags-input"
              ref={tagInputRef}
              className="tag-input__field"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleTagInputKeyDown}
              placeholder={form.tags.length === 0 ? 'Add a tag' : 'Add another tag'}
            />
          </div>
          {filteredSuggestions.length > 0 ? (
            <div className="tag-input__suggestions">
              <small>Suggestions:</small>
              {filteredSuggestions.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  className="chip tag-input__suggestion"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="form-row">
          <label>Visibility</label>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={form.visibility === 'public'}
                onChange={() => updateField('visibility', 'public')}
              />
              <span>
                Public <span className="label-optional">(visible to everyone, requires approval)</span>
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="visibility"
                value="team_restricted"
                checked={form.visibility === 'team_restricted'}
                onChange={() => updateField('visibility', 'team_restricted')}
              />
              <span>Limit to selected teams <span className="label-optional">(shared immediately)</span></span>
            </label>
          </div>
          {form.visibility === 'team_restricted' ? (
            <div className="card card-subtle">
              <p className="hint">
                Choose teams that should see this entry. You must be a member (admins can pick any team).
              </p>
              {teamError ? <p className="alert alert-warning">{teamError}</p> : null}
              {teams.length === 0 && !teamError ? (
                <p className="hint">You are not in any teams yet.</p>
              ) : null}
              <div className="checkbox-grid">
                {teams.map((team) => (
                  <label key={team.id} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={form.visibleTeamIds.includes(team.id)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setForm((current) => {
                          const nextIds = new Set(current.visibleTeamIds);
                          if (checked) {
                            nextIds.add(team.id);
                          } else {
                            nextIds.delete(team.id);
                          }
                          return { ...current, visibleTeamIds: Array.from(nextIds) };
                        });
                      }}
                    />
                    <span>{team.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="form-row">
          <label htmlFor="jsonText">{isNode ? 'Node' : 'Flow'} JSON</label>
          <div className="flow-form__json-tools">
            <label className="button button-secondary flow-form__json-upload">
              Upload JSON
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleJsonFileSelection}
              />
            </label>
            <span className="flow-form__json-hint">
              or drag a JSON file anywhere on this page
            </span>
          </div>
          {jsonImportError ? (
            <div className="flow-form__json-feedback flow-form__json-feedback--error">
              {jsonImportError}
            </div>
          ) : null}
          <textarea
            id="jsonText"
            rows={12}
            value={form.jsonText}
            onChange={(event) => updateField('jsonText', event.target.value)}
            placeholder='{\n  "steps": []\n}'
            required
          />
          <small>Ensure valid JSON - the flow will be validated on submission.</small>
          {jsonHelperMessage ? (
            <small className="flow-json-helper">{jsonHelperMessage}</small>
          ) : null}
        </div>

        {parseError ? (
          <div className="flow-structure flow-structure--error">{parseError}</div>
        ) : null}

        {!parseError && analysis ? (
          <div className="flow-structure">
            <div className="flow-structure__header">
              <div className="flow-structure__metrics">
                <span className="flow-structure__metric">
                  <strong>{analysis.nodeCount}</strong> nodes
                </span>
                <span className="flow-structure__metric">
                  <strong>{analysis.edges.length}</strong> connections
                </span>
                {typeof analysis.laneCount === 'number' ? (
                  <span className="flow-structure__metric">
                    <strong>{analysis.laneCount}</strong> lanes
                  </span>
                ) : null}
              </div>
              <div className="flow-structure__actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => {
                    setExtractionMode((value) => !value);
                    setSelectionError(null);
                  }}
                >
                  {extractionMode ? 'Close subset picker' : 'Extract subset'}
                </button>
              </div>
            </div>

            {analysis.warnings.length > 0
              ? analysis.warnings.map((warning) => (
                  <p key={warning} className="flow-structure__warning">
                    {warning}
                  </p>
                ))
              : null}

            {!extractionMode ? (
              <ul className="flow-structure__preview">
                {analysis.nodes.slice(0, 6).map((node) => (
                  <li key={node.id} className="flow-structure__preview-item">
                    <span>{node.label}</span>
                    {node.type ? (
                      <span className="flow-structure__preview-type">{node.type}</span>
                    ) : null}
                  </li>
                ))}
                {analysis.nodes.length > 6 ? (
                  <li className="flow-structure__preview-item flow-structure__preview-item--more">
                    +{analysis.nodes.length - 6} more nodes
                  </li>
                ) : null}
              </ul>
            ) : null}

            {extractionMode ? (
              <div className="flow-structure__panel">
                <div className="flow-structure__range">
              <div className="flow-structure__range-field">
                <label htmlFor="segment-start">Start node ID</label>
                <input
                  id="segment-start"
                  value={rangeStartId}
                  onChange={(event) => setRangeStartId(event.target.value)}
                  placeholder="e.g. 6"
                />
              </div>
              <div className="flow-structure__range-field">
                <label htmlFor="segment-end">End node ID</label>
                <input
                  id="segment-end"
                  value={rangeEndId}
                  onChange={(event) => setRangeEndId(event.target.value)}
                  placeholder="e.g. 238"
                />
              </div>
              <button
                type="button"
                className="button button-secondary flow-structure__range-action"
                onClick={handleSelectRange}
              >
                Select path
              </button>
            </div>
            <div className="flow-structure__controls">
              <button
                type="button"
                className="button button-tertiary"
                onClick={selectAllNodes}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="button button-tertiary"
                    onClick={clearNodeSelection}
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    className="button button-tertiary"
                    onClick={expandSelection}
                    disabled={selectedNodeCount === 0}
                  >
                    Expand to connected
                  </button>
                </div>
                <div className="flow-structure__selection-summary">
                  <span>{selectedNodeCount} selected</span>
                  <span>{selectedEdgeCount} connections retained</span>
                </div>
                {selectionError ? (
                  <div className="flow-structure__error">{selectionError}</div>
                ) : null}
                <ul className="flow-structure__list">
                  {analysis.nodes.map((node) => {
                    const outgoingLabels = node.outgoing
                      .map(
                        (edge) =>
                          analysis.nodeLookup.get(edge.targetId)?.label ?? edge.targetId
                      )
                      .filter(Boolean);
                    const incomingLabels = node.incoming
                      .map(
                        (edge) =>
                          analysis.nodeLookup.get(edge.targetId)?.label ?? edge.targetId
                      )
                      .filter(Boolean);

                    return (
                      <li key={node.id} className="flow-structure__list-item">
                        <label className="flow-structure__node">
                          <input
                            type="checkbox"
                            checked={selectedNodeIds.has(node.id)}
                            onChange={() => toggleNodeSelection(node.id)}
                          />
                          <span>
                            <strong>{node.label}</strong>
                            {node.type ? (
                              <span className="flow-structure__node-type">{node.type}</span>
                            ) : null}
                          </span>
                        </label>
                        <div className="flow-structure__connections">
                          <small>
                            {outgoingLabels.length > 0
                              ? `Outputs to ${outgoingLabels.join(', ')}`
                              : 'No outgoing connections'}
                          </small>
                          {incomingLabels.length > 0 ? (
                            <small>Inputs from {incomingLabels.join(', ')}</small>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="flow-structure__footer">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => {
                      setExtractionMode(false);
                      setSelectionError(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={handleApplyExtraction}
                    disabled={selectedNodeCount === 0}
                  >
                    Use selection
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}



        {mode === 'edit' ? (
          <div className="form-row">
            <label htmlFor="notes">Revision notes</label>
            <textarea
              id="notes"
              rows={3}
              value={form.notes || ''}
              onChange={(event) => updateField('notes', event.target.value)}
              placeholder="Highlight the changes for reviewers"
            />
          </div>
        ) : null}

        <div className="form-actions">
          <button
            className="button button-secondary"
            type="button"
            onClick={() => navigate(entry ? `/flows/${entry.id}` : '/my-entries')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button className="button" type="submit" disabled={submitting}>
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
